import { Feature, MultiPolygon, Polygon } from "geojson";

import { MAX_PLANNER_EXTENDED_ADDRESS } from "fond/project/polygon";
import { BoundaryCount, LayerId, LayerIds, Store } from "fond/types";

import { BaseDataLayerKey } from "../layers";

export type BoundaryImportKey = Extract<BaseDataLayerKey, "addresses" | "parcels" | "streets" | "aerial">;

/** Polygon select layers that can be imported from inside the boundary, keyed by base data layer. */
export const BOUNDARY_IMPORT_LAYER_IDS: Record<BoundaryImportKey, LayerId[]> = {
  addresses: [LayerIds.inAddress],
  parcels: [LayerIds.inParcel],
  // Polygon select imports planner extended streets from its underground path layer.
  streets: [LayerIds.inStreet],
  aerial: [LayerIds.inSpan, LayerIds.inPole],
};

export const BOUNDARY_IMPORT_KEYS = Object.keys(BOUNDARY_IMPORT_LAYER_IDS) as BoundaryImportKey[];

/** Layers passed to polygon select so that addresses, parcels and streets are imported by default. */
export const BOUNDARY_POLYGON_SELECT_LAYER_IDS: LayerId[] = [LayerIds.inParcel, LayerIds.inStreet];

/** Whether polygon select is open for the boundary rather than for a layer's area select. */
export const selectIsBoundarySelectOpen = (state: Store): boolean => state.project.polygon.isOpen && !state.project.activeLayerGroupId;

export type AddressCount = {
  count: number;
  /** Whether the boundary contains at least `count` addresses rather than exactly `count`. */
  isMinimum: boolean;
};

/** Return the number of addresses inside a boundary. */
export const getAddressCount = (boundaryCount: BoundaryCount | undefined): AddressCount | null => {
  if (!boundaryCount) return null;
  const { ExactCount, MinCount } = boundaryCount.Addresses;
  return ExactCount !== null ? { count: ExactCount, isMinimum: false } : { count: MinCount, isMinimum: true };
};

/** Return whether a boundary contains more addresses than a planner extended project allows. */
export const exceedsAddressLimit = (addressCount: AddressCount | null): boolean =>
  addressCount !== null && addressCount.count > MAX_PLANNER_EXTENDED_ADDRESS;

/** Return the polygon as a MultiPolygon feature, as stored in `version.UploadedArea`. */
export const toMultiPolygonFeature = (feature: Feature<Polygon | MultiPolygon>): Feature<MultiPolygon> =>
  feature.geometry.type === "MultiPolygon"
    ? (feature as Feature<MultiPolygon>)
    : { ...feature, geometry: { type: "MultiPolygon", coordinates: [feature.geometry.coordinates] } };
